import React, { useState, useEffect } from 'react';
import './../Styles/ProfileDialog.css'; 
import axios from 'axios';
import { useToast } from './ToastContext';
import { FiX, FiLock, FiEye, FiEyeOff, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';

const apiUrl = process.env.REACT_APP_API_URL;

const ProfileDialog = ({ employeeInfo, onClose, onUpdate }) => {
    const [formData, setFormData] = useState({ ...employeeInfo });
    const [managerName, setManagerName] = useState('');
    const showToast = useToast();

    // Password change state
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSavingPassword, setIsSavingPassword] = useState(false);

    useEffect(() => {
        setFormData({ ...employeeInfo });
    }, [employeeInfo]);

    useEffect(() => {
        const fetchManagerName = async () => {
            const employeeNumber = formData.employee_number || formData.employee_id;
            console.log('Fetching manager of employee:', employeeNumber);

            if (!employeeNumber) {
                console.error('No employee number available');
                return;
            }

            try {
                const response = await axios.get(`${apiUrl}/api/manager-emp/${employeeNumber}`);
                console.log('Fetched manager data:', response.data);
                setManagerName(response.data.full_name || 'Manager Not Assigned');
            } catch (error) {
                console.error('Error fetching manager name:', error);
                setManagerName('Error fetching manager');
            }
        };

        fetchManagerName();
    }, [formData.employee_number, formData.employee_id]);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({ ...prev, [name]: value }));
        if (passwordError) setPasswordError('');
        if (passwordSuccess) setPasswordSuccess('');
    };

    const handleCancelPasswordChange = () => {
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordError('');
        setPasswordSuccess('');
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setIsChangingPassword(false);
    };

    const handlePasswordSubmit = async () => {
        setPasswordError('');
        setPasswordSuccess('');
        const { currentPassword, newPassword, confirmPassword } = passwordData;

        if (!currentPassword) {
            setPasswordError('Current password is required.');
            return;
        }

        if (!newPassword) {
            setPasswordError('New password is required.');
            return;
        }

        if (!confirmPassword) {
            setPasswordError('Please confirm your new password.');
            return;
        }

        if (newPassword.length < 4) {
            setPasswordError('New password must be at least 4 characters long.');
            return;
        }

        if (currentPassword === newPassword) {
            setPasswordError('New password cannot be the same as the current password.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError('New password and confirm password do not match.');
            return;
        }

        setIsSavingPassword(true);
        try {
            const empId = formData.employee_number || formData.employee_id || localStorage.getItem('employeeId');
            const payload = {
                employeeId: empId,
                username: formData.username,
                currentPassword,
                newPassword,
                confirmPassword
            };

            const response = await axios.post(`${apiUrl}/api/change-password`, payload);
            if (response.status === 200) {
                setPasswordSuccess(response.data?.message || 'Password changed successfully!');
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                setTimeout(() => {
                    setIsChangingPassword(false);
                    setPasswordSuccess('');
                }, 2000);
            }
        } catch (error) {
            console.error('Error changing password:', error);
            const errMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to change password. Please check your current password.';
            setPasswordError(errMsg);
        } finally {
            setIsSavingPassword(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const hasChanges = Object.keys(formData).some(
            key => formData[key] !== (employeeInfo ? employeeInfo[key] : undefined)
        );

        if (!hasChanges) {
            showToast('No changes were made.', 'info');
            return;
        }

        try {
            const response = await axios.post(`${apiUrl}/api/update-user`, formData);

            if (response.status === 200) {
                console.log('User details updated successfully');
                showToast('Profile updated successfully!', 'success');
                if (onUpdate) {
                    onUpdate(formData);
                }
                onClose();
            } else {
                console.error('Failed to update user details');
                showToast('Failed to update profile.', 'error');
            }
        } catch (error) {
            console.error('Error updating user details:', error);
            showToast(error.response?.data?.message || 'Error updating profile.', 'error');
        }
    };

    // Helper to determine if an input should show error state
    const isCurrentPasswordInvalid = Boolean(
        passwordError && (
            passwordError.toLowerCase().includes('current password') ||
            (!passwordData.currentPassword && passwordError.toLowerCase().includes('required'))
        )
    );
    const isNewPasswordInvalid = Boolean(
        passwordError && (
            passwordError.toLowerCase().includes('new password') ||
            passwordError.toLowerCase().includes('characters') ||
            (!passwordData.newPassword && passwordError.toLowerCase().includes('required'))
        )
    );
    const isConfirmPasswordInvalid = Boolean(
        passwordError && (
            passwordError.toLowerCase().includes('confirm') ||
            passwordError.toLowerCase().includes('match')
        )
    );

    return (
        <div className="profile-dialog-overlay">
            <div className="profile-dialog-content">
                <div className="profile-dialog-header">
                    <h2>Profile Settings</h2>
                    <button
                        type="button"
                        className="profile-dialog-close-btn"
                        onClick={onClose}
                        aria-label="Close"
                        title="Close"
                    >
                        <FiX size={20} />
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="profile-dialog-grid">
                        <div className="profile-dialog-row">
                            <label htmlFor="fullName">Full Name</label>
                            <input
                                id="fullName"
                                type="text"
                                name="full_name"
                                value={formData.full_name || ''}
                                onChange={handleChange}
                                placeholder="Enter full name"
                            />
                        </div>
                        <div className="profile-dialog-row">
                            <label htmlFor="jobTitle">Job Title</label>
                            <input
                                id="jobTitle"
                                type="text"
                                name="job_title"
                                value={formData.job_title || ''}
                                onChange={handleChange}
                                placeholder="Enter job title"
                            />
                        </div>
                        <div className="profile-dialog-row">
                            <label htmlFor="employeeNumber">Employee ID</label>
                            <input
                                id="employeeNumber"
                                type="text"
                                name="employee_number"
                                value={formData.employee_number || ''}
                                onChange={handleChange}
                                placeholder="Enter employee ID"
                                disabled
                            />
                        </div>
                        <div className="profile-dialog-row">
                            <label htmlFor="email">Email</label>
                            <input
                                id="email"
                                type="email"
                                name="username"
                                value={formData.username || ''}
                                onChange={handleChange}
                                placeholder="Enter email"
                                disabled
                            />
                        </div>
                        <div className="profile-dialog-row">
                            <label htmlFor="department">Department</label>
                            <input
                                id="department"
                                type="text"
                                name="department"
                                value={formData.department || ''}
                                onChange={handleChange}
                                placeholder="Enter department"
                            />
                        </div>
                        <div className="profile-dialog-row">
                            <label htmlFor="project">Project</label>
                            <input
                                id="project"
                                type="text"
                                name="project"
                                value={formData.project_name || ''}
                                onChange={handleChange}
                                placeholder="Enter project"
                                disabled
                            />
                        </div>
                        <div className="profile-dialog-row">
                            <label htmlFor="location">Location</label>
                            <input
                                id="location"
                                type="text"
                                name="location"
                                value={formData.location || ''}
                                onChange={handleChange}
                                placeholder="Enter location"
                            />
                        </div>
                        <div className="profile-dialog-row">
                            <label htmlFor="reportingTo">Reporting to</label>
                            <input
                                id="reportingTo"
                                type="text"
                                value={managerName}
                                disabled
                            />
                        </div>
                    </div>

                    {/* Change Password Section */}
                    <div className="profile-password-section">
                        {!isChangingPassword ? (
                            <button
                                type="button"
                                className="change-password-toggle-btn"
                                onClick={() => {
                                    setIsChangingPassword(true);
                                    setPasswordError('');
                                    setPasswordSuccess('');
                                }}
                            >
                                <FiLock size={15} />
                                <span>Change Password</span>
                            </button>
                        ) : (
                            <div className="change-password-panel">
                                <div className="change-password-header">
                                    <div className="change-password-title">
                                        <FiLock size={16} />
                                        <span>Change Password</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="change-password-cancel-icon"
                                        onClick={handleCancelPasswordChange}
                                        title="Cancel"
                                        aria-label="Cancel password change"
                                    >
                                        <FiX size={16} />
                                    </button>
                                </div>

                                {/* Validation Error & Success Messages */}
                                {passwordError && (
                                    <div className="password-validation-error" role="alert">
                                        <FiAlertCircle size={15} style={{ flexShrink: 0 }} />
                                        <span>{passwordError}</span>
                                    </div>
                                )}
                                {passwordSuccess && (
                                    <div className="password-validation-success" role="status">
                                        <FiCheckCircle size={15} style={{ flexShrink: 0 }} />
                                        <span>{passwordSuccess}</span>
                                    </div>
                                )}

                                <div className="change-password-grid">
                                    <div className="profile-dialog-row">
                                        <label htmlFor="currentPassword">
                                            Current Password <span className="required-star">*</span>
                                        </label>
                                        <div className="password-input-wrapper">
                                            <input
                                                id="currentPassword"
                                                name="currentPassword"
                                                type={showCurrentPassword ? 'text' : 'password'}
                                                value={passwordData.currentPassword}
                                                onChange={handlePasswordChange}
                                                placeholder="Enter current password"
                                                autoComplete="current-password"
                                                className={isCurrentPasswordInvalid ? 'input-error' : ''}
                                            />
                                            <button
                                                type="button"
                                                className="password-eye-btn"
                                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                title={showCurrentPassword ? 'Hide password' : 'Show password'}
                                                tabIndex="-1"
                                            >
                                                {showCurrentPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="profile-dialog-row">
                                        <label htmlFor="newPassword">
                                            New Password <span className="required-star">*</span>
                                        </label>
                                        <div className="password-input-wrapper">
                                            <input
                                                id="newPassword"
                                                name="newPassword"
                                                type={showNewPassword ? 'text' : 'password'}
                                                value={passwordData.newPassword}
                                                onChange={handlePasswordChange}
                                                placeholder="Enter new password"
                                                autoComplete="new-password"
                                                className={isNewPasswordInvalid ? 'input-error' : ''}
                                            />
                                            <button
                                                type="button"
                                                className="password-eye-btn"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                title={showNewPassword ? 'Hide password' : 'Show password'}
                                                tabIndex="-1"
                                            >
                                                {showNewPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="profile-dialog-row">
                                        <label htmlFor="confirmPassword">
                                            Confirm Password <span className="required-star">*</span>
                                        </label>
                                        <div className="password-input-wrapper">
                                            <input
                                                id="confirmPassword"
                                                name="confirmPassword"
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={passwordData.confirmPassword}
                                                onChange={handlePasswordChange}
                                                placeholder="Confirm new password"
                                                autoComplete="new-password"
                                                className={isConfirmPasswordInvalid ? 'input-error' : ''}
                                            />
                                            <button
                                                type="button"
                                                className="password-eye-btn"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                title={showConfirmPassword ? 'Hide password' : 'Show password'}
                                                tabIndex="-1"
                                            >
                                                {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="change-password-actions">
                                    <button
                                        type="button"
                                        className="save-password-btn"
                                        onClick={handlePasswordSubmit}
                                        disabled={isSavingPassword}
                                    >
                                        {isSavingPassword ? 'Updating Password...' : 'Save Password'}
                                    </button>
                                    <button
                                        type="button"
                                        className="cancel-password-btn"
                                        onClick={handleCancelPasswordChange}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="form-actions">
                        <button type="submit">Update</button>
                        <button type="button" className="cancel" onClick={onClose}>Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProfileDialog;
